import { PhysioLayout } from "@/components/layout/physio-layout";
import { useState, useEffect, useCallback } from "react";
import { Star, ChevronDown, ChevronUp, Loader2, Save } from "lucide-react";

type PillarStatus = "developing" | "meeting" | "exceeding";

const STATUS_LABELS: Record<PillarStatus, string> = {
  developing: "Developing",
  meeting: "Meeting",
  exceeding: "Exceeding",
};

const STATUS_COLORS: Record<PillarStatus, string> = {
  developing: "bg-amber-50 text-amber-700 border-amber-200",
  meeting: "bg-blue-50 text-blue-700 border-blue-200",
  exceeding: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const APP_PILLARS = [
  {
    key: "clinical_practice",
    title: "Clinical Practice",
    description: "Highly developed clinical expertise and critical thinking in a defined area of practice.",
    competencies: [
      { key: "advanced_assessment", label: "Advanced clinical assessment and diagnostic reasoning" },
      { key: "complex_management", label: "Management of complex and multi-morbid presentations" },
      { key: "extended_scope", label: "Extended scope practice (e.g. imaging referral, injection therapy)" },
      { key: "outcome_monitoring", label: "Systematic outcome monitoring and clinical effectiveness evaluation" },
      { key: "safeguarding", label: "Safeguarding and risk management in complex cases" },
    ],
    colour: "emerald",
  },
  {
    key: "leadership",
    title: "Leadership & Management",
    description: "Leading services, teams, and systems to improve patient outcomes and organisational effectiveness.",
    competencies: [
      { key: "service_development", label: "Service development, redesign, and improvement" },
      { key: "team_leadership", label: "Leading and managing multidisciplinary teams" },
      { key: "strategic_planning", label: "Strategic planning and resource management" },
      { key: "workforce_development", label: "Workforce development and succession planning" },
      { key: "governance", label: "Clinical governance, audit, and quality assurance" },
    ],
    colour: "blue",
  },
  {
    key: "education",
    title: "Education",
    description: "Facilitating the learning and development of individuals and teams across the health system.",
    competencies: [
      { key: "teaching", label: "Teaching and facilitating learning for students and staff" },
      { key: "curriculum", label: "Contributing to curriculum and programme development" },
      { key: "supervision", label: "Clinical supervision and practice education" },
      { key: "assessment_learners", label: "Assessment of learner competence and progression" },
      { key: "cpd_facilitation", label: "Facilitating CPD and continuing education for the team" },
    ],
    colour: "violet",
  },
  {
    key: "research",
    title: "Research",
    description: "Generating and applying evidence to improve clinical practice and health outcomes.",
    competencies: [
      { key: "evidence_appraisal", label: "Critical appraisal and application of research evidence" },
      { key: "audit_qi", label: "Audit, service evaluation, and quality improvement" },
      { key: "research_participation", label: "Participation in clinical research projects" },
      { key: "dissemination", label: "Dissemination of findings (presentation, publication)" },
      { key: "implementation", label: "Research implementation and knowledge mobilisation" },
    ],
    colour: "amber",
  },
];

const COLOUR_MAP: Record<string, { header: string; icon: string; badge: string }> = {
  emerald: { header: "from-emerald-700 to-emerald-900", icon: "text-emerald-300", badge: "bg-emerald-100" },
  blue: { header: "from-blue-700 to-blue-900", icon: "text-blue-300", badge: "bg-blue-100" },
  violet: { header: "from-violet-700 to-violet-900", icon: "text-violet-300", badge: "bg-violet-100" },
  amber: { header: "from-amber-700 to-amber-900", icon: "text-amber-300", badge: "bg-amber-100" },
};

interface PortfolioEntry {
  frameworkKey: string;
  pillarKey: string;
  competencyKey: string;
  status: PillarStatus;
  evidenceNotes: string | null;
}

export default function AppPortfolio() {
  const [entries, setEntries] = useState<Record<string, PortfolioEntry>>({});
  const [openPillar, setOpenPillar] = useState<string | null>(APP_PILLARS[0].key);
  const [pendingEdits, setPendingEdits] = useState<Record<string, { status: PillarStatus; notes: string }>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await fetch("/api/physio/portfolio");
    if (res.ok) {
      const data: PortfolioEntry[] = await res.json();
      const map: Record<string, PortfolioEntry> = {};
      for (const e of data) {
        if (e.frameworkKey === "app") {
          map[`${e.pillarKey}:${e.competencyKey}`] = e;
        }
      }
      setEntries(map);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const getEntry = (pillarKey: string, competencyKey: string) =>
    entries[`${pillarKey}:${competencyKey}`];

  const getPending = (pillarKey: string, competencyKey: string) =>
    pendingEdits[`${pillarKey}:${competencyKey}`];

  const setPending = (pillarKey: string, competencyKey: string, field: "status" | "notes", value: string) => {
    const key = `${pillarKey}:${competencyKey}`;
    const existing = pendingEdits[key] ?? {
      status: getEntry(pillarKey, competencyKey)?.status ?? "developing",
      notes: getEntry(pillarKey, competencyKey)?.evidenceNotes ?? "",
    };
    setPendingEdits(prev => ({ ...prev, [key]: { ...existing, [field]: value } }));
  };

  const save = async (pillarKey: string, competencyKey: string) => {
    const key = `${pillarKey}:${competencyKey}`;
    const pending = pendingEdits[key];
    if (!pending) return;
    setSavingKey(key);
    try {
      const res = await fetch(`/api/physio/portfolio/app/${pillarKey}/${competencyKey}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: pending.status, evidenceNotes: pending.notes || undefined }),
      });
      if (res.ok) {
        await load();
        setPendingEdits(prev => { const n = { ...prev }; delete n[key]; return n; });
      }
    } finally {
      setSavingKey(null);
    }
  };

  const totalCompetencies = APP_PILLARS.reduce((s, p) => s + p.competencies.length, 0);
  const exceeding = Object.values(entries).filter(e => e.frameworkKey === "app" && e.status === "exceeding").length;
  const meeting = Object.values(entries).filter(e => e.frameworkKey === "app" && e.status === "meeting").length;
  const overall = Math.round(((exceeding * 2 + meeting) / (totalCompetencies * 2)) * 100);

  return (
    <PhysioLayout title="APP Portfolio Tracker">
      <div className="space-y-6">
        <div className="bg-gradient-to-br from-emerald-800 to-emerald-950 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-3">
            <Star className="h-8 w-8 text-emerald-300" />
            <div>
              <h2 className="text-xl font-bold">Advanced Physiotherapy Practitioner Portfolio</h2>
              <p className="text-emerald-300 text-sm">Four APP Pillars · HEE / NHSE APP Framework</p>
            </div>
          </div>
          <p className="text-emerald-200 text-sm leading-relaxed mb-4">
            Map evidence to the four Advanced Physiotherapy Practitioner pillars. Use this tracker to prepare for formal APP recognition, consultant physiotherapist roles, or structured peer review.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-2xl font-bold">{overall}%</p>
              <p className="text-xs text-emerald-300">Overall APP readiness</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xl font-bold">{meeting + exceeding}</p>
              <p className="text-xs text-emerald-300">Competencies evidenced</p>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <p className="text-xl font-bold">{exceeding}</p>
              <p className="text-xs text-emerald-300">Exceeding standard</p>
            </div>
          </div>
          <div className="mt-3 h-2 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-emerald-300 rounded-full transition-all" style={{ width: `${overall}%` }} />
          </div>
        </div>

        <div className="space-y-4">
          {APP_PILLARS.map((pillar) => {
            const c = COLOUR_MAP[pillar.colour];
            const pillarEntries = pillar.competencies.map(comp => getEntry(pillar.key, comp.key));
            const pillarMeeting = pillarEntries.filter(e => e?.status === "meeting" || e?.status === "exceeding").length;
            const isOpen = openPillar === pillar.key;

            return (
              <div key={pillar.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <button
                  onClick={() => setOpenPillar(isOpen ? null : pillar.key)}
                  className="w-full text-left"
                >
                  <div className={`bg-gradient-to-r ${c.header} p-5 text-white flex items-center justify-between`}>
                    <div>
                      <h3 className="font-bold text-lg">{pillar.title}</h3>
                      <p className={`text-sm ${c.icon} mt-0.5`}>{pillar.description}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold">{pillarMeeting}/{pillar.competencies.length}</p>
                        <p className={`text-xs ${c.icon}`}>evidenced</p>
                      </div>
                      {isOpen ? <ChevronUp className="h-5 w-5 text-white/70" /> : <ChevronDown className="h-5 w-5 text-white/70" />}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <div className="divide-y divide-slate-100">
                    {pillar.competencies.map((comp) => {
                      const entry = getEntry(pillar.key, comp.key);
                      const pending = getPending(pillar.key, comp.key);
                      const currentStatus = pending?.status ?? entry?.status ?? "developing";
                      const currentNotes = pending?.notes ?? entry?.evidenceNotes ?? "";
                      const saveKey = `${pillar.key}:${comp.key}`;
                      const hasPending = !!pending;

                      return (
                        <div key={comp.key} className="p-5">
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <p className="text-sm font-medium text-slate-800 flex-1">{comp.label}</p>
                            <select
                              value={currentStatus}
                              onChange={(e) => setPending(pillar.key, comp.key, "status", e.target.value as PillarStatus)}
                              className={`text-xs font-medium px-3 py-1.5 rounded-full border focus:outline-none focus:ring-2 focus:ring-emerald-500 ${STATUS_COLORS[currentStatus]}`}
                            >
                              {(["developing", "meeting", "exceeding"] as PillarStatus[]).map(s => (
                                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                              ))}
                            </select>
                          </div>
                          <textarea
                            value={currentNotes}
                            onChange={(e) => setPending(pillar.key, comp.key, "notes", e.target.value)}
                            placeholder="Add evidence notes, examples, or reflections..."
                            rows={2}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                          />
                          {hasPending && (
                            <div className="flex justify-end mt-2">
                              <button
                                onClick={() => save(pillar.key, comp.key)}
                                disabled={savingKey === saveKey}
                                className="flex items-center gap-1.5 text-xs bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-1.5 rounded-lg transition-colors"
                              >
                                {savingKey === saveKey ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                Save
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>APP Framework:</strong> Aligned with the NHS England / HEE Advanced Practitioner framework and the Chartered Society of Physiotherapy guidance on advanced practice. Formal APP recognition requires structured assessment and sign-off by an appropriate assessor or HEI; this tracker supports preparation only.
        </p>
      </div>
    </PhysioLayout>
  );
}
